class MainSearch extends SearchForm {
  constructor() {
    super();
    this.allSearchInputs = document.querySelectorAll('input[type="search"]');
    this.input.addEventListener('focus', this.onInputFocus.bind(this));
    this.allSearchInputs.forEach((input) => {
      input.addEventListener('input', (event) => {
        this.allSearchInputs.forEach((other) => {
          if (other !== event.target) other.value = event.target.value;
        });
      });
    });
  }

  onInputFocus() {
    if (window.innerWidth < 750) this.scrollIntoView({ behavior: 'smooth' });
  }
}

if (!customElements.get('main-search')) {
  customElements.define('main-search', MainSearch);
}

(() => {
  if (window.__DS_UNIVERSAL_SEARCH__) return;
  window.__DS_UNIVERSAL_SEARCH__ = true;

  if (window.location.pathname.replace(/\/+$/, '') !== '/search') return;

  const SOURCE_PAGE_SIZE = 120;
  const DISPLAY_PAGE_SIZE = 36;
  const MAX_SOURCE_PAGES = 100;
  const MAX_API_PAGES = 20;
  const INDEX_TTL = 90000;
  const INDEX_CACHE_KEY = 'ds-universal-quick-index-v4';
  const INDEX_LAST_KIND_KEY = 'ds-universal-last-index-kind-v1';
  const INDEX_SOURCE_HINT_TTL = 5 * 60 * 1000;
  const API_ENDPOINT = 'https://ypejzp-vz.myshopify.com/api/2026-07/graphql.json';
  // Token publico Storefront criado para esta loja; nunca usar token Admin aqui.
  const PUBLIC_STOREFRONT_TOKEN = 'c15646bac0a3c8f56dd0ab35cb3d8cf0';
  const ALLOWED_SORTS = new Set([
    'relevance', 'title-ascending', 'best-selling',
    'price-ascending', 'price-descending', 'created-descending'
  ]);

  let catalogPromise = null;
  let catalog = [];
  let sourceCards = new Map();
  let sourceMode = 'full';
  const sourcePagePromises = new Map();
  let activeIndexIsComplete = false;
  let currentMatches = [];
  let displayed = 0;
  let rendering = false;
  let runSequence = 0;
  let observer = null;
  let observerTimer = null;

  function normalize(value) {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  function termsOf(value) {
    return normalize(value).split(' ').filter(Boolean);
  }

  function originalQuery() {
    const params = new URLSearchParams(location.search);
    const legacy = params.get('ds_q') || params.get('ds_fixed');
    const raw = legacy || params.get('q') || '';
    return String(raw)
      .replace(/\b(?:title|vendor|product_type):/gi, '')
      .replace(/\*/g, '')
      .replace(/\s+(?:AND|OR)\s+/gi, ' ')
      .replace(/[()\"]/g, ' ')
      .trim();
  }

  function cleanUrl(displayQuery) {
    const url = new URL(location.href);
    url.searchParams.set('q', displayQuery);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    ['ds_smart', 'ds_q', 'ds_fixed', 'ds_alias_done',
     'ds_on_final', 'ds_stopword_fallback'].forEach((key) => url.searchParams.delete(key));
    if (url.href !== location.href) {
      history.replaceState(history.state, '', url.pathname + url.search + url.hash);
    }
  }


  const cardStyleSignatures = new Set();
  const cardLinkUrls = new Set();

  function preserveCardStyles(sourceDoc) {
    const nativeGrid = document.querySelector('#product-grid');
    const styleSources = [sourceDoc];
    if (nativeGrid) styleSources.push(nativeGrid);

    const nodes = styleSources.flatMap((root) => [
      ...root.querySelectorAll('style, link[rel="stylesheet"]')
    ]);

    nodes.forEach((source) => {
      if (source.matches('link[rel="stylesheet"]')) {
        const rawHref = source.getAttribute('href');
        if (!rawHref) return;
        let href;
        try { href = new URL(rawHref, location.href).href; }
        catch (_) { return; }
        if (cardLinkUrls.has(href)) return;
        cardLinkUrls.add(href);
        const existing = [...document.querySelectorAll('head link[rel="stylesheet"]')]
          .some((link) => link.href === href);
        if (existing) return;
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        if (source.media) link.media = source.media;
        document.head.appendChild(link);
        return;
      }

      const css = source.textContent.trim();
      if (!css || cardStyleSignatures.has(css)) return;
      cardStyleSignatures.add(css);
      if ([...document.head.querySelectorAll('style')]
          .some((existing) => existing.textContent.trim() === css)) return;
      const style = document.createElement('style');
      style.setAttribute('data-ds-preserved-card-style', '');
      style.textContent = css;
      document.head.appendChild(style);
    });
  }

  async function fetchSourcePage(page) {
    if (sourcePagePromises.has(page)) return sourcePagePromises.get(page);
    const promise = (async () => {
      const url = new URL('/collections/all', location.origin);
      url.searchParams.set('view', 'ds-search-cards');
      url.searchParams.set('page', String(page));
      url.searchParams.set('sort_by', 'best-selling');
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 18000);
      try {
        const response = await fetch(url.href, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
          signal: controller.signal
        });
        if (!response.ok) throw new Error(`Cards Shopify: HTTP ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        preserveCardStyles(doc);
        const root = doc.querySelector('[data-ds-source-page]');
        if (!root || Number(root.dataset.dsSourcePage) !== page) {
          throw new Error('Template auxiliar dos cards não encontrado.');
        }
        const cards = [...root.querySelectorAll('[data-ds-source-cards] > li[data-handle]')];
        const total = Number(root.dataset.dsSourceCount);
        return {
          page,
          cards,
          total: Number.isFinite(total) ? total : null,
          hasNext: root.dataset.dsSourceNext === '1'
        };
      } finally {
        clearTimeout(timeout);
      }
    })().catch((error) => {
      sourcePagePromises.delete(page);
      throw error;
    });
    sourcePagePromises.set(page, promise);
    return promise;
  }

  function addCards(batch) {
    for (const card of batch.cards) {
      const handle = card.dataset.handle;
      if (handle && !sourceCards.has(handle)) sourceCards.set(handle, card);
    }
  }

  function cacheRawProduct(card) {
    const data = card.querySelector('[data-ds-product-meta]');
    if (!data) throw new Error('Dados incompletos no card de origem.');
    return JSON.parse(data.textContent);
  }

  function getCachedIndex() {
    try {
      const stored = JSON.parse(sessionStorage.getItem(INDEX_CACHE_KEY) || 'null');
      if (!stored || stored.version !== 4 || Date.now() - stored.time > INDEX_TTL ||
          !Array.isArray(stored.products) || stored.products.length !== stored.total) return null;
      if (stored.kind !== 'api' && stored.kind !== 'source') return null;
      return stored;
    } catch (_) { return null; }
  }

  function cacheIndex(kind, products) {
    try {
      // Mantém uma pista pequena mesmo se o catálogo estourar a quota de storage.
      sessionStorage.setItem(INDEX_LAST_KIND_KEY, JSON.stringify({ kind, time: Date.now() }));
      sessionStorage.setItem(INDEX_CACHE_KEY, JSON.stringify({
        version: 4,
        time: Date.now(),
        kind,
        total: products.length,
        products
      }));
    } catch (_) { /* Busca continua funcionando sem cache. */ }
  }

  function recentlyNeededFullSource() {
    try {
      const last = JSON.parse(sessionStorage.getItem(INDEX_LAST_KIND_KEY) || 'null') ||
        JSON.parse(sessionStorage.getItem(INDEX_CACHE_KEY) || 'null');
      return last?.kind === 'source' && Date.now() - last.time < INDEX_SOURCE_HINT_TTL;
    } catch (_) { return false; }
  }

  function hasActiveFilters() {
    return [...new URLSearchParams(location.search).keys()]
      .some((key) => key.startsWith('filter.'));
  }

  function readIndexProduct(raw, rank) {
    if (!raw || !raw.handle || !raw.title) {
      throw new Error('Índice de produtos incompleto.');
    }
    return {
      handle: raw.handle,
      title: raw.title,
      vendor: raw.vendor || '',
      productType: raw.productType || '',
      tags: raw.tags || [],
      availableForSale: Boolean(raw.availableForSale),
      createdAt: Number(raw.createdAt || 0) * 1000,
      priceRange: { minVariantPrice: { amount: Number(raw.price) } },
      variants: {
        nodes: (raw.variants || []).map((variant) => ({
          availableForSale: Boolean(variant.availableForSale),
          price: { amount: Number(variant.price) },
          selectedOptions: (variant.options || []).map((value, i) => ({
            name: (raw.optionNames || [])[i] || '', value
          }))
        }))
      },
      _rank: rank
    };
  }

  async function fetchApiIndex() {
    const gql = `
      query DripIndex($after: String) {
        products(first: 250, after: $after, sortKey: BEST_SELLING) {
          nodes {
            handle title vendor productType availableForSale createdAt
            priceRange { minVariantPrice { amount } }
          }
          pageInfo { hasNextPage endCursor }
        }
      }
    `;
    const results = [];
    let after = null;
    // Limite total, não 11 s por página: falha rápida e usa a coleção.
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 6500);
    try {
      for (let page = 1; page <= MAX_API_PAGES; page++) {
        const response = await fetch(API_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': PUBLIC_STOREFRONT_TOKEN
          },
          body: JSON.stringify({ query: gql, variables: { after } }),
          signal: controller.signal
        });
        if (!response.ok) throw new Error(`Índice API: HTTP ${response.status}`);
        const payload = await response.json();
      if (payload.errors?.length || !payload.data?.products) {
        throw new Error('A Storefront API não devolveu o catálogo completo.');
      }
      const conn = payload.data.products;
      results.push(...conn.nodes.map((node) => ({
        handle: node.handle,
        title: node.title,
        vendor: node.vendor || '',
        productType: node.productType || '',
        availableForSale: node.availableForSale,
        createdAt: Math.floor(Date.parse(node.createdAt) / 1000),
        price: Number(node.priceRange.minVariantPrice.amount),
        // Os filtros de variantes/tags usam sempre o índice completo da coleção.
        tags: [], optionNames: [], variants: []
      })));
      if (!conn.pageInfo.hasNextPage) return results;
      if (!conn.pageInfo.endCursor || conn.pageInfo.endCursor === after) {
        throw new Error('Cursor de paginação da API inválido.');
      }
      after = conn.pageInfo.endCursor;
    }
      throw new Error('Índice API excedeu o limite de segurança.');
    } finally {
      clearTimeout(timeout);
    }
  }

  function verifySourceOrder(index, first) {
    if (first.total !== index.length || !first.cards.length) return false;
    if (first.hasNext && first.cards.length !== SOURCE_PAGE_SIZE) return false;
    if (new Set(index.map((p) => p.handle)).size !== index.length) return false;
    // Verifica TODOS os 120 itens da primeira página para impedir que
    // uma ordem parecida seja tratada como identidade do catálogo.
    return first.cards.every((card, i) => card.dataset.handle === index[i]?.handle);
  }

  async function loadFullSource(first, query, sequence) {
    const total = first.total;
    const pages = total === null ? null : Math.ceil(total / SOURCE_PAGE_SIZE);
    if (pages !== null && pages > MAX_SOURCE_PAGES) {
      throw new Error('Catálogo ultrapassou o limite de páginas dos cards.');
    }
    // Reconstrói o catálogo sempre na ordem das páginas, inclusive quando
    // uma tentativa anterior já tinha carregado páginas fora de ordem.
    const ordered = new Map();
    const appendOrdered = (batch) => {
      for (const card of batch.cards) {
        if (card.dataset.handle && !ordered.has(card.dataset.handle)) {
          ordered.set(card.dataset.handle, card);
        }
      }
      addCards(batch);
    };
    appendOrdered(first);
    let next = 2;
    let lastPage = pages;
    while (lastPage === null || next <= lastPage) {
      if (next > MAX_SOURCE_PAGES) {
        throw new Error('O catálogo ultrapassou o limite de páginas dos cards.');
      }
      const end = lastPage === null ? next : Math.min(next + 4, lastPage);
      // Baixa lotes em paralelo para reduzir a espera pelo índice completo.
      const batches = await Promise.all(
        Array.from({ length: end - next + 1 }, (_, i) => fetchSourcePage(next + i))
      );
      for (const batch of batches) {
        appendOrdered(batch);
        if (lastPage === null && !batch.hasNext) lastPage = batch.page;
      }
      next = end + 1;
    }
    sourceCards = ordered;
    if (total !== null && sourceCards.size !== total) {
      throw new Error(`Catálogo incompleto: ${sourceCards.size} de ${total}.`);
    }
    const all = [...sourceCards.values()].map(cacheRawProduct);
    cacheIndex('source', all);
    return { mode: 'source', items: all.map(readIndexProduct) };
  }

  async function getCatalog(query, sequence, forceSource = false) {
    const cached = getCachedIndex();
    if (!forceSource && cached && (!hasActiveFilters() || cached.kind === 'source')) {
      // Já conhecemos o catálogo e sua ordem: carrega apenas as páginas com
      // cards necessários e valida essas páginas em ensureMatchingCards().
      return { mode: cached.kind, items: cached.products.map(readIndexProduct), fromCache: true };
    }
    const skipKnownSlowApi = !forceSource && recentlyNeededFullSource();
    const canTryApi = !forceSource && !hasActiveFilters() && !cached && !skipKnownSlowApi;
    // API leve e primeiro lote de cards em paralelo, sem esperar a primeira
    // página HTML terminar para começar a obter os títulos do catálogo.
    const apiCandidate = canTryApi
      ? fetchApiIndex().then(
          (index) => ({ index }),
          (error) => ({ error })
        )
      : null;
    const first = await fetchSourcePage(1);
    addCards(first);
    // Se nas últimas buscas a API não estava sincronizada com a ordem Shopify,
    // não espera 6,5s novamente antes de usar a fonte que já funcionou.
    if (skipKnownSlowApi) return loadFullSource(first, query, sequence);
    // Se o cache expirou ou foi invalidado, tenta a API antes do fallback.
    const candidate = apiCandidate || (
      !forceSource && !hasActiveFilters()
        ? await fetchApiIndex().then(
            (index) => ({ index }),
            (error) => ({ error })
          )
        : null
    );
    if (candidate) {
      const result = await candidate;
      if (result.index && verifySourceOrder(result.index, first)) {
        cacheIndex('api', result.index);
        return { mode: 'api', items: result.index.map(readIndexProduct) };
      }
      if (result.error) {
        console.info('[Drip Busca] Índice leve indisponível; usando a coleção original.', result.error.message);
      } else {
        console.info('[Drip Busca] API e coleção têm publicações/ordem diferentes; mantendo índice da coleção.');
      }
    }
    return loadFullSource(first, query, sequence);
  }

  async function ensureMatchingCards(products) {
    const missing = products.filter((item) => !sourceCards.has(item.handle));
    if (!missing.length) return;
    // _rank representa a posição do catálogo ordenado por best-selling
    // somente depois de validarmos a equivalência do índice com o Liquid.
    const pages = [...new Set(missing.map((p) =>
      Math.floor(p._rank / SOURCE_PAGE_SIZE) + 1
    ))];
    const groups = [];
    for (let i = 0; i < pages.length; i += 4) {
      groups.push(pages.slice(i, i + 4));
    }
    for (const group of groups) {
      const batches = await Promise.all(group.map(fetchSourcePage));
      for (const batch of batches) {
        if (batch.total !== catalog.length) {
          throw new Error('Catálogo alterado durante o carregamento; atualize a busca.');
        }
        const expected = catalog.slice(
          (batch.page - 1) * SOURCE_PAGE_SIZE,
          batch.page * SOURCE_PAGE_SIZE
        );
        if (batch.cards.length !== expected.length ||
            !batch.cards.every((card, i) => card.dataset.handle === expected[i]?.handle)) {
          throw new Error('A página de cards não corresponde ao índice; atualize a busca.');
        }
        addCards(batch);
      }
    }
    if (missing.some((p) => !sourceCards.has(p.handle))) {
      throw new Error('Índice e cards não correspondem: o catálogo precisa ser relido.');
    }
  }

  function readProductMetadata(card, rank) {
    const node = card.querySelector('[data-ds-product-meta]');
    if (!node) throw new Error('Um produto da Shopify está sem dados de catálogo.');
    const raw = JSON.parse(node.textContent);
    if (!raw.handle || !raw.title || !Array.isArray(raw.variants)) {
      throw new Error('Dados de produto inválidos no catálogo Shopify.');
    }
    const names = raw.optionNames || [];
    return {
      handle: raw.handle,
      title: raw.title,
      vendor: raw.vendor || '',
      productType: raw.productType || '',
      tags: raw.tags || [],
      availableForSale: Boolean(raw.availableForSale),
      createdAt: Number(raw.createdAt || 0) * 1000,
      priceRange: { minVariantPrice: { amount: Number(raw.price) } },
      variants: {
        nodes: raw.variants.map((variant) => ({
          availableForSale: Boolean(variant.availableForSale),
          price: { amount: Number(variant.price) },
          selectedOptions: (variant.options || []).map((option, index) => ({
            name: names[index] || '',
            value: option
          }))
        }))
      },
      _rank: rank
    };
  }

  function titleAndVendorWords(product) {
    const title = termsOf(product.title);
    const vendor = termsOf(product.vendor);
    const type = termsOf(product.productType);
    const vendorInitials = vendor.map((word) => word[0]).join('');
    return { title, vendor, type, vendorInitials };
  }

  function textMatch(product, queryTokens, exactShort) {
    const words = titleAndVendorWords(product);
    const searchable = [...words.title, ...words.vendor];
    return queryTokens.every((token) => {
      if (words.vendorInitials === token && token.length >= 2) return true;
      if (token.length <= 2 && exactShort) {
        return searchable.some((word) => word === token);
      }
      return searchable.some((word) => word.startsWith(token));
    });
  }

  function abbreviationMatch(product, token) {
    if (token.length < 2 || token.length > 5) return false;
    const sequences = [termsOf(product.title), termsOf(product.vendor)];
    for (const words of sequences) {
      for (let start = 0; start < words.length - 1; start += 1) {
        let initials = '';
        for (let length = 1; length <= 5 && start + length <= words.length; length += 1) {
          initials += words[start + length - 1][0];
          if (length >= 2 && initials === token) return true;
        }
      }
    }
    return false;
  }

  function score(product, queryTokens) {
    const title = normalize(product.title);
    const vendor = normalize(product.vendor);
    const titleTokens = termsOf(product.title);
    let points = 0;
    const phrase = queryTokens.join(' ');
    if (title === phrase) points += 150;
    if (title.startsWith(phrase + ' ')) points += 70;
    if (vendor === phrase) points += 45;
    queryTokens.forEach((token) => {
      const position = titleTokens.findIndex((word) => word.startsWith(token));
      if (position !== -1) {
        points += titleTokens[position] === token ? 25 : 15;
        points += Math.max(0, 8 - position);
      } else if (termsOf(product.vendor).some((word) => word.startsWith(token))) {
        points += 12;
      }
    });
    return points;
  }

  function queryResults(query) {
    const tokens = termsOf(query);
    if (!tokens.length) return [];
    const published = activeIndexIsComplete ? catalog : catalog.filter((product) => sourceCards.has(product.handle));
    const exactShort = published.some((product) =>
      textMatch(product, tokens, true)
    );
    const direct = published.filter((product) => textMatch(product, tokens, exactShort));
    if (direct.length) return direct;
    return published.filter((product) => tokens.every((token) =>
      textMatch(product, [token], true) || abbreviationMatch(product, token)
    ));
  }

  function isFiltered(product, params) {
    const entries = [...params.entries()].filter(([key]) => key.startsWith('filter.'));
    const groups = new Map();
    for (const [key, value] of entries) {
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(value);
    }
    const min = Number(groups.get('filter.v.price.gte')?.[0]);
    const max = Number(groups.get('filter.v.price.lte')?.[0]);
    const variants = product.variants?.nodes || [];
    const available = variants.filter((variant) => variant.availableForSale);
    const price = Number(product.priceRange?.minVariantPrice?.amount);

    for (const [key, values] of groups) {
      if (key === 'filter.v.price.gte' && values[0] && price < min) return false;
      if (key === 'filter.v.price.lte' && values[0] && price > max) return false;
      if (key === 'filter.v.availability' && values.includes('1') && !product.availableForSale) return false;
      if (key === 'filter.p.vendor' && !values.some((val) => normalize(val) === normalize(product.vendor))) return false;
      if (key === 'filter.p.product_type' && !values.some((val) => normalize(val) === normalize(product.productType))) return false;
      if (key === 'filter.p.tag' && !values.some((val) => product.tags.some((tag) => normalize(tag) === normalize(val)))) return false;
      if (key.startsWith('filter.v.option.')) {
        const option = normalize(key.replace('filter.v.option.', ''));
        const ok = available.some((variant) =>
          (variant.selectedOptions || []).some((selected) =>
            normalize(selected.name) === option &&
            values.some((value) => normalize(value) === normalize(selected.value))
          )
        );
        if (!ok) return false;
      }
    }
    return true;
  }

  function getSort() {
    const val = new URLSearchParams(location.search).get('sort_by') || 'relevance';
    return ALLOWED_SORTS.has(val) ? val : 'relevance';
  }

  function sortResults(results, tokens) {
    const mode = getSort();
    results.sort((a, b) => {
      if (mode === 'title-ascending') {
        return a.title.localeCompare(b.title, 'pt-BR', { numeric: true });
      }
      if (mode === 'best-selling') return a._rank - b._rank;
      if (mode === 'price-ascending' || mode === 'price-descending') {
        const direction = mode === 'price-ascending' ? 1 : -1;
        return direction * (
          Number(a.priceRange.minVariantPrice.amount) -
          Number(b.priceRange.minVariantPrice.amount)
        ) || a._rank - b._rank;
      }
      if (mode === 'created-descending') {
        return b.createdAt - a.createdAt || a._rank - b._rank;
      }
      return score(b, tokens) - score(a, tokens) || a._rank - b._rank;
    });
    return results;
  }

  function updateHeading(query, count) {
    document.querySelectorAll('.ds-search-toolbar__title').forEach((node) => {
      node.textContent = `Resultados para “${query}”`;
    });
    document.querySelectorAll('.ds-search-toolbar__count').forEach((node) => {
      node.textContent = `${count} ${count === 1 ? 'modelo' : 'modelos'}`;
    });
    document.querySelectorAll('#Search-In-Template').forEach((node) => {
      node.value = query;
    });
    document.querySelectorAll('.ds-search-sort__item[data-ds-sort]').forEach((button) => {
      button.classList.toggle('is-active', button.dataset.dsSort === getSort());
    });
  }

  async function renderBatch(reset, sequence = runSequence) {
    const grid = document.querySelector('#product-grid');
    const pager = document.querySelector('[data-ds-universal-pager]');
    const empty = document.querySelector('[data-ds-search-empty]');
    if (!grid || !pager) throw new Error('Grade da Drip não encontrada.');
    if (rendering && !reset) return;
    rendering = true;
    const offset = reset ? 0 : displayed;
    const batch = currentMatches.slice(offset, offset + DISPLAY_PAGE_SIZE);
    const button = pager.querySelector('button');
    if (button) button.disabled = true;
    try {
      await ensureMatchingCards(batch);
      if (sequence !== runSequence) return;
      const fragment = document.createDocumentFragment();
      for (const product of batch) {
        const original = sourceCards.get(product.handle);
        if (!original) throw new Error(`Card não encontrado: ${product.handle}`);
        const card = original.cloneNode(true);
        card.dataset.dsUniversal = '1';
        fragment.appendChild(card);
      }
      if (reset) grid.replaceChildren();
      grid.appendChild(fragment);
      displayed = offset + batch.length;
      if (empty) empty.hidden = currentMatches.length > 0;
      document.querySelector('[data-ds-search-template]')
        ?.classList.toggle('template-search--empty', currentMatches.length === 0);
      const progress = pager.querySelector('[data-ds-universal-progress]');
      if (progress) progress.textContent = `Você viu ${displayed} de ${currentMatches.length} modelos`;
      pager.hidden = displayed >= currentMatches.length || !currentMatches.length;
      if (button) {
        button.style.removeProperty('display');
        button.disabled = false;
      }
      document.documentElement.classList.remove('ds-universal-pending');
      document.documentElement.classList.add('ds-universal-ready');
      document.dispatchEvent(new CustomEvent('ds:universal-search:rendered', {
        detail: { count: currentMatches.length, displayed }
      }));
    } finally {
      rendering = false;
    }
  }

  function showError(error) {
    console.error('[Drip Universal Search]', error);
    rendering = true;
    const grid = document.querySelector('#product-grid');
    if (grid) grid.replaceChildren();
    const empty = document.querySelector('[data-ds-search-empty]');
    if (empty) empty.hidden = true;
    const pager = document.querySelector('[data-ds-universal-pager]');
    if (pager) pager.hidden = true;
    const container = document.querySelector('#ProductGridContainer');
    if (container) {
      let message = container.querySelector('.ds-universal-failure');
      if (!message) {
        message = document.createElement('div');
        message.className = 'ds-universal-failure';
        container.prepend(message);
      }
      message.replaceChildren();
      const copy = document.createElement('p');
      copy.textContent = 'Não foi possível carregar os resultados agora. Tente novamente.';
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = 'Tentar novamente';
      button.addEventListener('click', () => {
        catalogPromise = null;
        sourceCards = new Map();
        sourcePagePromises.clear();
        try { sessionStorage.removeItem(INDEX_CACHE_KEY); } catch (_) {}
        executeSearch(true);
      });
      message.append(copy, button);
    }
    updateHeading(originalQuery(), 0);
    document.documentElement.classList.remove('ds-universal-pending');
    document.documentElement.classList.add('ds-universal-ready');
    requestAnimationFrame(() => { rendering = false; });
  }

  async function executeSearch(forceSource = false) {
    const sequence = ++runSequence;
    const started = Date.now();
    const query = originalQuery();
    if (!query) return;
    cleanUrl(query);
    // Mantém a grade indisponível até o índice completo e a ordenação final
    // estarem prontos. Nunca exibe resultados intermediários que serão trocados.
    document.documentElement.classList.add('ds-universal-pending');
    document.documentElement.classList.remove('ds-universal-ready');
    document.querySelector('.ds-universal-failure')?.remove();
    if (hasActiveFilters() && sourceMode === 'api') forceSource = true;
    const attemptedFullFallback = forceSource;
    let usedWarmCache = false;
    try {
      if (forceSource) catalogPromise = null;
      if (!catalogPromise) {
        catalogPromise = getCatalog(query, sequence, forceSource).catch((error) => {
          catalogPromise = null;
          throw error;
        });
      }
      const index = await catalogPromise;
      if (sequence !== runSequence) return;
      usedWarmCache = Boolean(index.fromCache);
      sourceMode = index.mode;
      catalog = index.items;
      activeIndexIsComplete = true;
      const filtered = queryResults(query).filter((product) =>
        isFiltered(product, new URLSearchParams(location.search))
      );
      currentMatches = sortResults(filtered, termsOf(query));
      // Não anuncia a contagem até todos os produtos terem sido indexados.
      await renderBatch(true, sequence);
      if (sequence !== runSequence) return;
      updateHeading(query, currentMatches.length);
      console.info(`[Drip Busca] ${currentMatches.length} resultados; índice ${sourceMode}${usedWarmCache ? ' (cache)' : ''}; ${Date.now() - started} ms.`);
    } catch (error) {
      if (sequence !== runSequence) return;
      if (!attemptedFullFallback && (sourceMode !== 'source' || usedWarmCache)) {
        console.info('[Drip Busca] Validando novamente o índice completo.', error.message);
        try { sessionStorage.removeItem(INDEX_CACHE_KEY); } catch (_) {}
        catalogPromise = null;
        try {
          await executeSearch(true);
          return;
        } catch (fullError) { error = fullError; }
      }
      showError(error);
    }
  }

  function bindUi() {
    const pager = document.querySelector('[data-ds-universal-pager]');
    pager?.querySelector('[data-ds-universal-more]')?.addEventListener('click', async (event) => {
      event.currentTarget.disabled = true;
      try {
        await renderBatch(false);
      } catch (error) {
        console.info('[Drip Busca] Revalidando cards antes de carregar mais.', error.message);
        catalogPromise = null;
        try { sessionStorage.removeItem(INDEX_CACHE_KEY); } catch (_) {}
        executeSearch(true);
      }
    });

    document.addEventListener('click', (event) => {
      const button = event.target.closest('.ds-search-sort__item[data-ds-sort]');
      if (!button) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const mode = button.dataset.dsSort;
      if (!ALLOWED_SORTS.has(mode)) return;
      const url = new URL(location.href);
      if (mode === 'relevance') url.searchParams.delete('sort_by');
      else url.searchParams.set('sort_by', mode);
      url.searchParams.delete('page');
      history.pushState(history.state, '', url.pathname + url.search + url.hash);
      document.querySelector('.ds-search-sort')?.removeAttribute('open');
      executeSearch();
    }, true);

    document.addEventListener('submit', (event) => {
      const form = event.target.closest('form[action*="/search"]');
      if (!form) return;
      const input = form.querySelector('input[name="q"]');
      if (!input || !input.value.trim()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const url = new URL('/search', location.origin);
      url.searchParams.set('q', input.value.trim());
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      // Esta página já tem o índice completo na memória. Aproveita-o para
      // buscas seguintes, sem recarregar HTML nem alterar a lógica dos resultados.
      history.pushState(history.state, '', url.pathname + url.search);
      executeSearch();
    }, true);

    addEventListener('popstate', () => executeSearch());
    if (observer) observer.disconnect();
    // facets.js costuma substituir o UL inteiro. A nova versão não renderiza
    // cards provisórios, portanto precisamos detectar uma grade vazia substituída.
    let lastGrid = document.querySelector('#product-grid');
    observer = new MutationObserver(() => {
      if (rendering) return;
      const grid = document.querySelector('#product-grid');
      if (!grid) return;
      if (grid !== lastGrid) {
        lastGrid = grid;
        if (originalQuery()) {
          document.documentElement.classList.add('ds-universal-pending');
          document.documentElement.classList.remove('ds-universal-ready');
          clearTimeout(observerTimer);
          observerTimer = setTimeout(executeSearch, 140);
        }
        return;
      }
      if (!currentMatches.length) return;
      const first = grid.querySelector('li.grid__item');
      if (!first || first.dataset.dsUniversal === '1') return;
      clearTimeout(observerTimer);
      observerTimer = setTimeout(executeSearch, 140);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  const start = () => {
    if (!originalQuery()) return;
    bindUi();
    executeSearch();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
